import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { EmptyState, Loader, Pagination } from '@lms/ui';
import { PERMISSIONS } from '@lms/shared-types';
import { SearchEmptyState } from '@/components/SearchEmptyState';
import { SearchInput } from '@/components/SearchInput';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { useDebouncedSearch } from '@/hooks/useDebouncedValue';
import { usePermissions } from '@/hooks/usePermissions';
import { LibraryObjectCard } from '@/components/library/LibraryObjectCard';
import { api, listResource } from '@/lib/api';

interface LibraryObject {
  id: string;
  title: string;
  type?: string;
  status?: string;
  currentVersion?: { versionNumber?: number };
  sectionItems?: unknown[];
}

interface CategoryOption {
  id: string;
  name: string;
}

const TYPE_LABELS: Record<string, string> = {
  rich_text: 'Texto enriquecido',
  video: 'Video',
  pdf: 'Documento PDF',
  pdf_folder: 'Carpeta de PDF',
  image: 'Imagen',
  audio: 'Audio',
  download: 'Archivo descargable',
  html: 'HTML',
  external_link: 'Enlace externo',
  h5p: 'H5P',
  assessment: 'Evaluación',
  scorm: 'SCORM',
  xapi: 'xAPI',
  cmi5: 'cmi5',
};

const STATUS_LABELS: Record<string, string> = {
  draft: 'Borrador',
  published: 'Publicado',
  archived: 'Archivado',
};

export function LibraryPage() {
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission(PERMISSIONS.BIBLIOTECA_CREAR);
  const { value: search, setValue: setSearch, debounced, page, setPage } = useDebouncedSearch();
  const [type, setType] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [tagId, setTagId] = useState('');
  const perPage = 12;
  const appliedSearch = debounced.trim();

  useEffect(() => {
    setPage(1);
  }, [type, setPage]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['library', type, appliedSearch, page],
    queryFn: () =>
      listResource<LibraryObject>('/library', {
        page,
        perPage,
        ...(type ? { type } : {}),
        ...(appliedSearch ? { search: appliedSearch } : {}),
      }),
  });

  const { data: categoriesTree } = useQuery({
    queryKey: ['categories'],
    queryFn: () => api<CategoryOption[]>('/categories'),
  });

  const rows = data?.data ?? [];
  const categories = useMemo(() => {
    const flat: CategoryOption[] = [];
    function walk(nodes: CategoryOption[]) {
      for (const node of nodes) {
        flat.push(node);
        const children = (node as CategoryOption & { children?: CategoryOption[] }).children;
        if (children?.length) walk(children);
      }
    }
    walk(categoriesTree ?? []);
    return flat;
  }, [categoriesTree]);

  const totalObjects = data?.meta?.total ?? rows.length;
  const totalPages = data?.meta?.lastPage ?? Math.max(1, Math.ceil(totalObjects / perPage));
  const paged = rows;
  const hasQuery = Boolean(appliedSearch || type);

  return (
    <div className="lms-library-index lms-site-admin-hub-page font-sans">
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div className="flex items-start gap-4">
          <HubBackLink to="/administracion?tab=courses" label="Volver a Administración" />
          <div>
            <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Biblioteca de Contenidos</h1>
            <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
              Repositorio central de objetos de aprendizaje reutilizables
            </p>
          </div>
        </div>

        {canCreate && (
          <Link to="/library/create" className="lib-index-btn-primary">
            <span className="material-symbols-outlined text-xl" aria-hidden="true">
              add
            </span>
            Nuevo objeto
          </Link>
        )}
      </div>

      <form method="get" action="/library" className="lib-index-filters mb-8" onSubmit={(event) => event.preventDefault()}>
        <div className="lib-index-filters-grid grid gap-4">
          <div className="flex min-w-0 flex-col gap-1.5">
            <label htmlFor="library-search" className="lib-index-label">
              Buscar
            </label>
            <SearchInput
              id="library-search"
              name="search"
              value={search}
              onChange={setSearch}
              placeholder="Buscar por título..."
              aria-label="Buscar por título"
            />
          </div>

          <div className="flex min-w-0 flex-col gap-1.5">
            <label htmlFor="library-type" className="lib-index-label">
              Tipo
            </label>
            <select
              id="library-type"
              name="type"
              className="lib-index-select"
              value={type}
              onChange={(e) => {
                setType(e.target.value);
                setPage(1);
              }}
            >
              <option value="">Todos</option>
              {Object.entries(TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex min-w-0 flex-col gap-1.5">
            <label htmlFor="library-category" className="lib-index-label">
              Categoría
            </label>
            <select
              id="library-category"
              name="category_id"
              className="lib-index-select"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
            >
              <option value="">Todas</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex min-w-0 flex-col gap-1.5">
            <label htmlFor="library-tag" className="lib-index-label">
              Etiqueta
            </label>
            <select
              id="library-tag"
              name="tag_id"
              className="lib-index-select"
              value={tagId}
              onChange={(e) => setTagId(e.target.value)}
            >
              <option value="">Todas</option>
            </select>
          </div>

          <div className="lib-index-filters-actions flex flex-wrap items-end gap-3">
            <button
              type="button"
              className="lib-index-btn-clear flex-1 sm:flex-none"
              onClick={() => {
                setSearch('');
                setType('');
                setCategoryId('');
                setTagId('');
              }}
            >
              Limpiar
            </button>
          </div>
        </div>
      </form>

      <p className="mb-6 text-sm font-medium text-dash-on-surface-variant">
        {totalObjects.toLocaleString('es')} {totalObjects === 1 ? 'objeto en la biblioteca' : 'objetos en la biblioteca'}
      </p>

      {error && (
        <p className="mb-4 text-sm text-red-600" role="alert">
          {(error as Error).message}
        </p>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader />
        </div>
      ) : paged.length === 0 ? (
        hasQuery ? (
          <SearchEmptyState
            term={appliedSearch}
            onClear={() => {
              setSearch('');
              setType('');
              setCategoryId('');
              setTagId('');
            }}
          />
        ) : (
          <EmptyState
            title="Biblioteca vacía"
            description="Crea objetos de aprendizaje para reutilizarlos en múltiples cursos."
            actionLabel={canCreate ? 'Crear objeto' : undefined}
            onAction={canCreate ? () => { window.location.href = '/library/create'; } : undefined}
          />
        )
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {paged.map((object) => (
            <LibraryObjectCard
              key={object.id}
              item={{
                id: object.id,
                title: object.title,
                type: object.type,
                typeLabel: TYPE_LABELS[object.type ?? ''] ?? object.type,
                status: object.status,
                statusLabel: STATUS_LABELS[object.status ?? ''] ?? object.status,
                sectionItems: object.sectionItems,
                currentVersion: object.currentVersion,
              }}
            />
          ))}

          {canCreate && (
            <Link to="/library/create" className="lib-index-create-card group">
              <div className="lib-index-create-card__icon" aria-hidden="true">
                <span className="material-symbols-outlined text-[2rem]">add_circle</span>
              </div>
              <div className="text-center">
                <span className="block text-sm font-semibold text-dash-on-surface">Crear nuevo</span>
                <span className="block text-xs text-dash-on-surface-variant">Añadir recurso a la biblioteca</span>
              </div>
            </Link>
          )}
        </div>
      )}

      {!isLoading && totalPages > 1 && (
        <div className="mt-8">
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </div>
      )}
    </div>
  );
}
